<script setup lang="ts">
import { PV } from "@/widgets";
import { isNotNil } from "es-toolkit";
import MainIcon from "~icons/mdi/wan";
import type { WANParam } from "../../common/types.common";
import { booleanOptions, wanModeOptions } from "../../data/options";
import { defineActions, defineState } from "../common";
import { FormInputNumeric, FormInputSelect, FormInputText } from "../input";
import { FormMessage, FormPanel, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = WANParam;
interface PropsType extends BaseProps<ModelType> {
    isDirty?: boolean;
    isCreated?: boolean;
    isDeleted?: boolean;
    bindings: string[];
    onDelete?: () => Promise<void>;
    placeholder?: {
        [K in keyof ModelType]: string;
    };
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    isDirty: false,
    isCreated: false,
    isDeleted: false,
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => {
        return true;
    },
});

function mapBinding(name: string): string | undefined {
    const regexLANTR098 = /InternetGatewayDevice\.LANDevice\.\d+\.LANEthernetInterfaceConfig\.(?<num>\d+)/i;
    const regexSSIDTR098 = /InternetGatewayDevice\.LANDevice\.\d+\.WLANConfiguration\.(?<num>\d+)/i;
    let match: RegExpExecArray | null | undefined;

    if (isNotNil((match = regexLANTR098.exec(name)))) {
        return `LAN${match.groups!["num"]}`;
    } else if (isNotNil((match = regexSSIDTR098.exec(name)))) {
        return `SSID${match.groups!["num"]}`;
    }

    return undefined;
}

const actions = defineActions(model, props, state);
</script>

<template>
    <FormPanel :param="props">
        <template #header>
            <span class="inline-flex flex-row items-center gap-3">
                <MainIcon class="ml-1" />
                <span class="font-semibold text-gray-600 inline-flex flex-row gap-4 items-center">
                    {{ props.label }}{{ props.isDirty ? "*" : "" }}
                    <template v-if="props.onDelete !== undefined">
                        <PV.Button
                            v-show="!props.isDeleted && !props.isCreated"
                            icon="pi pi-trash"
                            label="Hapus"
                            severity="danger"
                            size="small"
                            text
                            @click="props.onDelete"
                        />
                    </template>
                </span>
            </span>
        </template>

        <template #icons>
            <span class="inline-flex flex-row gap-2">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <div v-if="state.hasError" class="p-4">
            <FormMessage :state="state" />
        </div>

        <PV.Message v-if="props.isDeleted" class="mb-4" icon="pi pi-info-circle" severity="error">
            WAN ini sedang berada dalam proses penghapusan.
        </PV.Message>
        <PV.Message v-else-if="props.isCreated" class="mb-4" icon="pi pi-info-circle" severity="info">
            WAN ini sedang berada dalam tahapan konfigurasi.
        </PV.Message>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <FormInputText v-model="model.name" embedMode="embed" label="Nama" mode="static" />
            <FormInputSelect
                v-model="model.mode"
                label="Mode"
                :mode="model.possibleModes.length > 1 ? state.nestMode : 'static'"
                :options="wanModeOptions"
            />
            <FormInputText
                v-show="model.type === 'PPP' && model.mode === 'Route'"
                v-model="model.username"
                embedMode="embed"
                label="Username"
                :mode="state.nestMode"
            />
            <FormInputText
                v-show="model.type === 'PPP' && model.mode === 'Route'"
                v-model="model.password"
                embedMode="embed"
                label="Password"
                :mode="state.nestMode"
                :placeholder="model.passwordKnown ? undefined : 'TIDAK DIKETAHUI'"
            />
            <FormInputSelect
                v-model="model.useVlan"
                label="Gunakan VLAN"
                :mode="state.nestMode"
                :options="booleanOptions"
            />
            <FormInputNumeric
                v-show="model.useVlan"
                v-model="model.vlan"
                label="VLAN ID"
                :max="4080"
                :min="10"
                :mode="state.nestMode"
            />
        </div>

        <div v-if="props.bindings.length > 0" class="flex flex-row gap-4 w-full mt-6 items-end">
            <div class="font-semibold">Bindings</div>
            <div v-for="b in props.bindings">
                <input checked disabled type="checkbox" />
                <label class="ml-1">{{ mapBinding(b) }}</label>
            </div>
        </div>
    </FormPanel>
</template>
